// ✅ src/pages/VehicleV2/AddRetreadedTyre.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/addRetreadedTyre", addRetreadedTyre);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/addRetreadedTyre/config
//   GET  https://nissi-india.com/api/addRetreadedTyre/dropdowns?branchId=..
//   GET  https://nissi-india.com/api/addRetreadedTyre/vehicle-codes?branchId=..&RetTyreVI=..&mode=normal
//   GET  https://nissi-india.com/api/addRetreadedTyre/tyre-serials?branchId=..&RetTyreVI=..&type=E|N&vehicleId=..&source=..
//   GET  https://nissi-india.com/api/addRetreadedTyre/view?retreadedTyreId=..
//   GET  https://nissi-india.com/api/addRetreadedTyre/serial-details?serialId=..&serialNo=..
//   POST https://nissi-india.com/api/addRetreadedTyre/save
//
// ✅ Updated:
// - Sends x-finyear / x-finyearid headers for inventory DB resolver
// - Follows same style/pattern as AddNewTyre.jsx
// - Edit mode improved for serial preselection
// - Keeps existing/new tyre radio logic

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaTimes, FaUndo } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addReteredTyre";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

// UI date input gives yyyy-mm-dd; backend expects dd/MM/yyyy
const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const txt = String(iso).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(txt)) return "";
  const [yyyy, mm, dd] = txt.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

// backend view gives dd/MM/yyyy; date input expects yyyy-mm-dd
const ddmmyyyyToISO = (s) => {
  if (!s) return "";
  const txt = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(txt)) return txt;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(txt);
  if (!m) return "";
  const dd = m[1];
  const mm = m[2];
  const yyyy = m[3];
  return `${yyyy}-${mm}-${dd}`;
};

const AddRetreadedTyre = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const editId = useMemo(
    () =>
      toInt(
        searchParams.get("retreadTyreId") ||
          searchParams.get("retreadedTyreId") ||
          searchParams.get("RetrededTyreID") ||
          0
      ),
    [searchParams]
  );

  const urlType = useMemo(
    () => toStr(searchParams.get("type")).toUpperCase(),
    [searchParams]
  );

  // localStorage values
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchType = localStorage.getItem("branchtype") || "HO";
  const branchId = Number(localStorage.getItem("branchid") || 8);

  // ✅ for inventory resolver headers
  const finYear =
    localStorage.getItem("financialYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("finYear") ||
    "";

  const finYearId =
    localStorage.getItem("financialYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("finYearId") ||
    localStorage.getItem("financialyearid") ||
    "";

  const [loading, setLoading] = useState(false);
  const [retTyreVI, setRetTyreVI] = useState(0);

  const [vehicleCodes, setVehicleCodes] = useState([]);
  const [serials, setSerials] = useState([]);
  const [retreadFromList, setRetreadFromList] = useState([]);
  const [noOfRetreadList, setNoOfRetreadList] = useState([]);

  const RETREAD_TYPES = useMemo(
    () => [
      { id: "Hot", name: "Hot" },
      { id: "Cold", name: "Cold" },
    ],
    []
  );

  const POSITIONS = useMemo(
    () => [
      { id: "Front", name: "Front" },
      { id: "Rear", name: "Rear" },
    ],
    []
  );

  const [formData, setFormData] = useState({
    tyreType: "existing", // existing => E, new => N
    dateISO: new Date().toISOString().split("T")[0],
    tyreSizePlyRating: "",
    typeOfRetread: "",
    retreadedDateISO: "",
    tyreAmount: "",

    vehicleId: "select",
    serialId: "Select",
    serialNoText: "",

    position: "",
    noOfRetread: "",
    retreadedFrom: "",
    retreadedFromText: "",
  });

  // ✅ axios headers for inventory db resolver
  const inventoryHeaders = useMemo(() => {
    const headers = {};

    if (toStr(finYear)) headers["x-finyear"] = toStr(finYear);
    if (toStr(finYearId)) {
      headers["x-finyearid"] = toStr(finYearId);
      headers["x-finyear-id"] = toStr(finYearId);
    }

    return headers;
  }, [finYear, finYearId]);

  const axiosConfigWithHeaders = useMemo(
    () => ({
      headers: inventoryHeaders,
    }),
    [inventoryHeaders]
  );

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const showSerialDropdown = useMemo(() => {
    if (formData.tyreType === "existing") return true;
    return retTyreVI === 1;
  }, [formData.tyreType, retTyreVI]);

  const effectiveSerialNo = useMemo(() => {
    if (showSerialDropdown) {
      const opt = serials.find(
        (x) => String(x.id) === String(formData.serialId)
      );
      return opt ? String(opt.name || "") : "";
    }
    return toStr(formData.serialNoText);
  }, [showSerialDropdown, formData.serialId, formData.serialNoText, serials]);

  const isRetreadFromNew = useMemo(
    () => toStr(formData.retreadedFrom) === "New",
    [formData.retreadedFrom]
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "tyreType") {
        next.serialId = "Select";
        next.serialNoText = "";
        next.tyreSizePlyRating = "";
        next.position = "";
        next.tyreAmount = "";
        next.noOfRetread = "";
      }

      if (name === "vehicleId") {
        next.serialId = "Select";
        next.serialNoText = "";
        next.tyreSizePlyRating = "";
        next.position = "";
        next.tyreAmount = "";
        next.noOfRetread = "";
      }

      if (name === "retreadedFrom" && value !== "New") {
        next.retreadedFromText = "";
      }

      return next;
    });
  };

  const renderOptions = (arr, valueKey = "id", labelKey = "name") =>
    (arr || []).map((o) => (
      <option
        key={`${o[valueKey]}`}
        value={String(o[valueKey])}
        disabled={o.enabled === false}
      >
        {String(o[labelKey])}
      </option>
    ));

  /** ---------------- API helpers ---------------- */

  const loadSerials = async ({
    vi = retTyreVI,
    vehicleId = 0,
    tyreType = formData.tyreType,
    source,
  } = {}) => {
    const type = tyreType === "existing" ? "E" : "N";

    if (vi === 1) {
      if (!vehicleId) {
        setSerials([]);
        return [];
      }

      const r = await axios.get(`${API_BASE}/tyre-serials`, {
        params: {
          branchId,
          RetTyreVI: 1,
          type,
          vehicleId,
          source: source || "vehicleIssue",
        },
        headers: inventoryHeaders,
      });

      const rows = Array.isArray(r.data?.serials) ? r.data.serials : [];
      setSerials(rows);
      return rows;
    }

    const r = await axios.get(`${API_BASE}/tyre-serials`, {
      params: {
        branchId,
        RetTyreVI: 0,
        type,
      },
      headers: inventoryHeaders,
    });

    const rows = Array.isArray(r.data?.serials) ? r.data.serials : [];
    setSerials(rows);
    return rows;
  };

  const loadSerialDetails = async (serialId, serialNo) => {
    if (!serialNo) return;

    const r = await axios.get(`${API_BASE}/serial-details`, {
      params: { serialId, serialNo },
      headers: inventoryHeaders,
    });

    const data = r.data?.data;
    if (!data) return;

    setFormData((prev) => ({
      ...prev,
      tyreSizePlyRating: data.tyreSize || prev.tyreSizePlyRating,
      position: ["Front", "Rear"].includes(String(data.tyrePosition))
        ? String(data.tyrePosition)
        : prev.position,
      tyreAmount:
        data.amount !== undefined ? String(data.amount || "") : prev.tyreAmount,
    }));

    if (Array.isArray(data.noOfRetreadedRemaining) && data.noOfRetreadedRemaining.length) {
      setNoOfRetreadList(
        data.noOfRetreadedRemaining.map((x) => ({
          value: String(x.value),
          label: String(x.label),
          enabled: x.enabled !== false,
        }))
      );
    }
  };

  const loadBase = async () => {
    const cfg = await axios.get(`${API_BASE}/config`, axiosConfigWithHeaders);
    const vi = toInt(cfg.data?.RetTyreVI, 0);
    setRetTyreVI(vi);

    const dd = await axios.get(`${API_BASE}/dropdowns`, {
      params: { branchId, branchType, RetTyreVI: vi },
      headers: inventoryHeaders,
    });

    setRetreadFromList(
      Array.isArray(dd.data?.reterdFrom) ? dd.data.reterdFrom : []
    );
    setNoOfRetreadList(
      Array.isArray(dd.data?.noOfRetreaded) ? dd.data.noOfRetreaded : []
    );

    const vc = await axios.get(`${API_BASE}/vehicle-codes`, {
      params: { branchId, RetTyreVI: vi, mode: "normal" },
      headers: inventoryHeaders,
    });

    setVehicleCodes(Array.isArray(vc.data?.vehicleCodes) ? vc.data.vehicleCodes : []);

    return vi;
  };

  /** ---------------- init ---------------- */

  useEffect(() => {
    const init = async () => {
      try {
        if (!branchId) {
          toast.error("branchId missing");
          return;
        }

        setLoading(true);
        const vi = await loadBase();

        if (editId > 0) {
          const r = await axios.get(`${API_BASE}/view`, {
            params: { retreadedTyreId: editId },
            headers: inventoryHeaders,
          });

          const data = r.data?.data;
          if (!data) {
            toast.error("Record not found");
            return;
          }

          const typeEorN =
            urlType === "E" || urlType === "N"
              ? urlType
              : toStr(data.type || "E").toUpperCase();

          const tyreType = typeEorN === "E" ? "existing" : "new";

          const loadedSerials = await loadSerials({
            vi,
            vehicleId: toInt(data.vehicleId),
            tyreType,
            source: vi === 1 ? "vehicleIssue" : undefined,
          });

          let matchedSerialId = "Select";
          const serialText = toStr(data.tyreSerialNo);

          if (Array.isArray(loadedSerials) && serialText) {
            const match = loadedSerials.find(
              (x) => toStr(x.name) === serialText
            );
            if (match) matchedSerialId = String(match.id);
          }

          setFormData((prev) => ({
            ...prev,
            tyreType,
            dateISO: ddmmyyyyToISO(data.date) || prev.dateISO,
            tyreSizePlyRating: data.tyreSize || "",
            typeOfRetread: ["Hot", "Cold"].includes(String(data.retreadType))
              ? String(data.retreadType)
              : "",
            retreadedDateISO: ddmmyyyyToISO(data.retreadedDate) || "",
            tyreAmount: data.tyreAmount ? String(data.tyreAmount) : "",
            vehicleId: data.vehicleId ? String(data.vehicleId) : "select",
            serialId: matchedSerialId,
            serialNoText: data.tyreSerialNo || "",
            position: ["Front", "Rear"].includes(String(data.tyrePosition))
              ? String(data.tyrePosition)
              : "",
            noOfRetread: data.noOfRetreaded
              ? String(data.noOfRetreaded)
              : "",
            retreadedFrom: data.retreadedFrom || "",
            retreadedFromText: "",
          }));
        } else {
          await loadSerials({ vi, vehicleId: 0, tyreType: formData.tyreType });
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, branchId, branchType, urlType, axiosConfigWithHeaders, inventoryHeaders]);

  /** ---------------- VI mode: vehicle/type change -> serials ---------------- */
  useEffect(() => {
    const run = async () => {
      try {
        if (retTyreVI !== 1) return;

        const vId = toInt(formData.vehicleId);
        if (!vId) {
          setSerials([]);
          return;
        }

        await loadSerials({
          vi: 1,
          vehicleId: vId,
          tyreType: formData.tyreType,
        });
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load serials");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retTyreVI, formData.vehicleId, formData.tyreType, inventoryHeaders]);

  /** ---------------- serial details ---------------- */
  useEffect(() => {
    const run = async () => {
      try {
        if (!showSerialDropdown) return;

        const sid = String(formData.serialId || "");
        if (!sid || sid === "Select" || sid === "select") return;

        const opt = serials.find((x) => String(x.id) === String(sid));
        const serialNo = opt ? String(opt.name || "") : "";
        if (!serialNo) return;

        await loadSerialDetails(sid, serialNo);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load serial details");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSerialDropdown, formData.serialId, inventoryHeaders]);

  /** ---------------- save ---------------- */
  const validate = () => {
    if (!formData.dateISO) return "Date is required";
    if (!formData.tyreSizePlyRating) return "Tyre Size/Ply Rating is required";
    if (!formData.typeOfRetread) return "Type of Retread is required";
    if (!formData.vehicleId || formData.vehicleId === "select") return "Vehicle Code is required";
    if (!effectiveSerialNo) return "Tyre Serial No is required";
    if (!formData.position) return "Position is required";
    if (!formData.noOfRetread) return "No of Retread is required";
    if (!formData.retreadedFrom) return "Retreaded From is required";
    if (isRetreadFromNew && !toStr(formData.retreadedFromText)) return "Enter Retreaded From";
    if (!toStr(formData.tyreAmount)) return "Tyre Amount is required";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        retreadedTyreId: editId > 0 ? editId : 0,
        branchId,
        branchType,
        RetTyreVI: retTyreVI,
        type: formData.tyreType === "existing" ? "E" : "N",
        date: isoToDDMMYYYY(formData.dateISO),
        vehicleId: formData.vehicleId,

        serialId: showSerialDropdown ? formData.serialId : "Select",
        tyreSerialNo: effectiveSerialNo,

        tyreSize: formData.tyreSizePlyRating,
        tyrePosition: formData.position,
        retreadType: formData.typeOfRetread,
        noOfRetreaded: formData.noOfRetread,
        retreadedDate: formData.retreadedDateISO
          ? isoToDDMMYYYY(formData.retreadedDateISO)
          : "",
        retreadedFrom: formData.retreadedFrom,
        retreadedFromText: formData.retreadedFromText,
        tyreAmount: formData.tyreAmount,
        loginUserId,
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: inventoryHeaders,
      });

      if (r.data?.success) {
        toast.success(
          r.data?.message ||
            (editId > 0 ? "Updated Successfully" : "Inserted Successfully")
        );
        navigate("/vehicle/TyreDetails");
      } else {
        toast.error(r.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      if (editId > 0) {
        navigate(0);
        return;
      }

      setSerials([]);
      setFormData({
        tyreType: "existing",
        dateISO: new Date().toISOString().split("T")[0],
        tyreSizePlyRating: "",
        typeOfRetread: "",
        retreadedDateISO: "",
        tyreAmount: "",
        vehicleId: "select",
        serialId: "Select",
        serialNoText: "",
        position: "",
        noOfRetread: "",
        retreadedFrom: "",
        retreadedFromText: "",
      });

      const vi = await loadBase();
      await loadSerials({ vi, vehicleId: 0, tyreType: "existing" });

      toast.info("Form has been reset");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Reset failed");
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADD RETREADED TYRE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-4">
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {editId > 0 ? `Edit Retreaded Tyre (ID: ${editId})` : "Add Retreaded Tyre"}
                </h1>
                {loading && (
                  <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Loading...
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FaTimes className="w-4 h-4" />
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSave className="w-4 h-4" />
                  {editId > 0 ? "Update Retreaded Tyre" : "Save Retreaded Tyre"}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6 mb-8">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="tyreType"
                  value="existing"
                  checked={formData.tyreType === "existing"}
                  onChange={handleInputChange}
                />
                Existing Tyre
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="tyreType"
                  value="new"
                  checked={formData.tyreType === "new"}
                  onChange={handleInputChange}
                />
                New Tyre
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Date *</label>
                  <input
                    type="date"
                    name="dateISO"
                    value={formData.dateISO}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Size/Ply Rating *</label>
                  <input
                    type="text"
                    name="tyreSizePlyRating"
                    value={formData.tyreSizePlyRating}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter tyre size/ply rating"
                  />
                </div>

                <div>
                  <label className={labelClass}>Type of Retread *</label>
                  <select
                    name="typeOfRetread"
                    value={formData.typeOfRetread}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {renderOptions(RETREAD_TYPES)}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Retreaded Date</label>
                  <input
                    type="date"
                    name="retreadedDateISO"
                    value={formData.retreadedDateISO}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tyre Amount *</label>
                  <input
                    type="text"
                    name="tyreAmount"
                    value={formData.tyreAmount}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Vehicle Code *</label>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="select">Select</option>
                    {renderOptions(vehicleCodes)}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Tyre Serial No *</label>
                  {showSerialDropdown ? (
                    <select
                      name="serialId"
                      value={formData.serialId}
                      onChange={handleInputChange}
                      className={selectClass}
                    >
                      <option value="Select">Select</option>
                      {renderOptions(serials)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="serialNoText"
                      value={formData.serialNoText}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter serial number"
                    />
                  )}
                </div>

                <div>
                  <label className={labelClass}>Position *</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {renderOptions(POSITIONS)}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>No of Retread *</label>
                  <select
                    name="noOfRetread"
                    value={formData.noOfRetread}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {(noOfRetreadList || []).map((o) => (
                      <option
                        key={String(o.value)}
                        value={String(o.value)}
                        disabled={o.enabled === false}
                      >
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Retreaded From *</label>
                  <select
                    name="retreadedFrom"
                    value={formData.retreadedFrom}
                    onChange={handleInputChange}
                    className={selectClass}
                  >
                    <option value="">Select</option>
                    {renderOptions(retreadFromList)}
                  </select>

                  {isRetreadFromNew && (
                    <div className="mt-2">
                      <label className={labelClass}>Enter Retreaded From *</label>
                      <input
                        type="text"
                        name="retreadedFromText"
                        value={formData.retreadedFromText}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="Enter retreaded from"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`my-8 border-t ${isDark ? "border-gray-700" : "border-gray-300"}`} />

            <div className="flex justify-center gap-4 mt-8">
              <button
                type="button"
                onClick={handleReset}
                className={`flex items-center gap-2 px-8 py-3 border rounded-lg font-medium ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaUndo className="w-4 h-4" />
                {editId > 0 ? "Reload Form" : "Reset Form"}
              </button>

              <button
                type="submit"
                disabled={loading}
                className={`px-8 py-3 ${buttonClass} ${hoverClass} text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {editId > 0 ? "Update Retreaded Tyre" : "Save Retreaded Tyre"}
              </button>
            </div>
          </form>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default AddRetreadedTyre;